import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Observable, of, Subscription } from 'rxjs';
import { CustomerService } from 'src/app/_services/customer.service';
import { Collaborator } from 'src/app/_types/auth.types';
import { BusinessAccidentMetadata } from 'src/app/_types/lead.types';
import {
  BUSINESS_ACCIDENT_INSURANCE_COVERAGE_VALUES,
  getBusinessAccidentInsuranceCoverageLabel,
} from 'src/app/enums/business/business-accident-insurance-coverage.enum';
import {
  BUSINESS_WAITING_DAYS_VALUES,
  getBusinessWaitingDaysLabel,
} from 'src/app/enums/business/business-waiting-days.enum';
import { InsuranceType } from 'src/app/enums/insurance-type.enum';
import {
  BUSINESS_INSURANCE_PAYMENT_FREQUENCY,
  getPaymentFrequencyLabel,
} from 'src/app/enums/payment-frequency.enum';

import { LeadFormBase } from '../../lead-form-base';
import { SharedBusinessFormComponent } from '../shared-business-form/shared-business-form.component';

@Component({
  selector: 'app-business-accident-form',
  templateUrl: './business-accident-form.component.html',
  styleUrls: ['./business-accident-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
    SharedBusinessFormComponent,
  ],
})
export class BusinessAccidentFormComponent
  extends LeadFormBase
  implements OnDestroy
{
  @Input()
  formGroup!: FormGroup;

  @Input()
  userId!: string;

  hasInsuranceSubscription!: Subscription;

  hasCollectiveContractSubscription!: Subscription;

  coveragesSelectSubscription!: Subscription;

  premiumSurplusParticipationSubscription!: Subscription;

  collaboratorsCoverageSubscription!: Subscription;

  collaborators: Collaborator[] = [];

  // Separate FormControl for the coverages mat-select
  coveragesSelectControl: FormControl = new FormControl([]);

  constructor(
    private readonly fb: FormBuilder,
    private readonly customerService: CustomerService
  ) {
    super();
  }

  sharedBusinessFormGroup: FormGroup = this.fb.group({
    activityArea: [null],
    activityDescription: [null],
    hasLaborAgreement: [null],
    laborAgreement: [null],
    isInternational: [null],
    activityCountries: [null],
    hasSubcontractors: [null],
    collaboratorsCount: [null],
    yearlyAnnualRevenue: [null],
    yearlySalaries: [null],
  });

  metadataFormGroup: FormGroup = this.fb.group({
    type: InsuranceType.BUSINESS_ACCIDENT,
    professionalMenTotalSalary: [null],
    professionalWomenTotalSalary: [null],
    nonProfessionalMenTotalSalary: [null],
    nonProfessionalWomenTotalSalary: [null],
    hasCollectiveContract: [null],
    collectiveContract: [null],
    hasInsurance: [null],
    currentInsurer: [null],
    hasClaimsLastFiveYears: [null],
    suvaOnly: [null],
    baseInsurance: [null],
    complementaryInsurance: [null],
    startDate: [null],
    contractDuration: [null],
    paymentFrequency: [null],
    splitPaymentFee: [null],
    hasclaimsLastFiveYears: [null],
    allCollaboratorsCovered: [false],
    insuredPersons: this.fb.array<FormGroup>([]),
    womenCount: [null],
    menCount: [null],
    coverages: this.fb.group({
      firstSecondDay: [null],
      insuredBenefitsFirstTwoDays: [null],
      insuredBenefitsThirdDay: [null],
      waitingDelay: [null],
      maximumInsuredSalary: [null],
      invalidityPension: [null],
      survivalPensionDuration: [null],
      excessSalaries: [null],
      coverages: [null],
      coveragesWaitingDelay: [null],
      privateTreatmentFees: [null],
      capitalInvalidityLaaSalary: [null],
      capitalInvalidityExcessSalary: [null],
      deathBenefitLaaSalary: [null],
      deathBenefitExcessSalary: [null],
      apRate: [null],
      aanpRate: [null],
    }),
    additionalCoverages: this.fb.group({
      relapseCases: [null],
      guaranteedPremiumRate: [null],
      grossNegligence: [null],
      claimTerminationWaiver: [null],
      premiumSurplusParticipation: [null],
      premiumSurplusComputingPeriod: [null],
    }),
    sharedBusiness: this.sharedBusinessFormGroup,
  });

  override ngOnInit(): void {
    super.ngOnInit();

    if (!this.userId) {
      throw new Error('User ID is required');
    }
    this.customerService
      .getCollaboratorsByUser(this.userId)
      .subscribe((res) => {
        this.collaborators = res.data.collaborators;
      });
    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      const businessMetadata = this.metadata as BusinessAccidentMetadata;

      // Load shared business data
      if (businessMetadata.activityArea) {
        this.sharedBusinessFormGroup.patchValue({
          activityArea: businessMetadata.activityArea,
          activityDescription: businessMetadata.activityDescription,
          hasLaborAgreement: businessMetadata.hasLaborAgreement,
          laborAgreement: businessMetadata.laborAgreement,
          isInternational: businessMetadata.isInternational,
          activityCountries: businessMetadata.activityCountries,
          hasSubcontractors: businessMetadata.hasSubcontractors,
          collaboratorsCount: businessMetadata.collaboratorsCount,
          yearlyAnnualRevenue: businessMetadata.yearlyAnnualRevenue,
          yearlySalaries: businessMetadata.yearlySalaries,
        });
      }

      // Load insured persons
      if (
        businessMetadata.insuredPersons &&
        businessMetadata.insuredPersons.length > 0
      ) {
        businessMetadata.insuredPersons.forEach((person) => {
          this.addInsuredPerson(person.fullName, person.collaboratorId);
        });
      }
      this.metadataFormGroup.patchValue(businessMetadata);
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    // Handle conditional fields
    this.hasInsuranceSubscription = this.metadataFormGroup.controls[
      'hasInsurance'
    ].valueChanges.subscribe((value) => {
      if (value !== 'true' && value !== true) {
        this.metadataFormGroup.controls['currentInsurer'].reset();
      }
    });

    this.hasCollectiveContractSubscription = this.metadataFormGroup.controls[
      'hasCollectiveContract'
    ].valueChanges.subscribe((value) => {
      if (value !== 'true' && value !== true) {
        this.metadataFormGroup.controls['collectiveContract'].reset();
      }
    });

    // Handle premiumSurplusParticipation conditional field
    const additionalCoveragesGroup = this.metadataFormGroup.controls?.[
      'additionalCoverages'
    ] as FormGroup;
    this.premiumSurplusParticipationSubscription =
      additionalCoveragesGroup.controls[
        'premiumSurplusParticipation'
      ].valueChanges.subscribe((value) => {
        if (
          value !== 'true' &&
          value !== true &&
          additionalCoveragesGroup?.controls?.['premiumSurplusComputingPeriod']
        ) {
          additionalCoveragesGroup.controls[
            'premiumSurplusComputingPeriod'
          ].reset();
        }
      });

    this.collaboratorsCoverageSubscription = this.metadataFormGroup.controls[
      'allCollaboratorsCovered'
    ].valueChanges.subscribe((value) => {
      if (value) {
        (
          this.metadataFormGroup.controls[
            'insuredPersons'
          ] as FormArray<FormGroup>
        ).clear();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.hasInsuranceSubscription) {
      this.hasInsuranceSubscription.unsubscribe();
    }
    if (this.hasCollectiveContractSubscription) {
      this.hasCollectiveContractSubscription.unsubscribe();
    }
    if (this.premiumSurplusParticipationSubscription) {
      this.premiumSurplusParticipationSubscription.unsubscribe();
    }
    if (this.collaboratorsCoverageSubscription) {
      this.collaboratorsCoverageSubscription.unsubscribe();
    }
    this.coveragesSelectControl.reset();
  }

  // Insured persons management
  get insuredPersons(): FormArray<FormGroup> {
    return this.metadataFormGroup.get('insuredPersons') as FormArray<FormGroup>;
  }

  addInsuredPerson(fullName: string = '', id?: string): void {
    const personForm = this.fb.group({
      fullName: [fullName, Validators.required],
      collaboratorId: [id || null],
    });
    this.insuredPersons.push(personForm);
  }

  removeInsuredPerson(index: number): void {
    this.insuredPersons.removeAt(index);
  }

  onCollaboratorSelected(index: number, collaboratorId: string | null): void {
    const personForm = this.insuredPersons.at(index) as FormGroup;
    if (!collaboratorId) {
      // Clear collaborator data if "Sélectionner" is selected
      personForm.patchValue({
        collaboratorId: null,
        isCollaborator: false,
      });
      return;
    }
    const collaborator = this.collaborators.find(
      (c) => c.id === collaboratorId
    );
    if (collaborator) {
      personForm.patchValue({
        fullName: `${collaborator.firstName} ${collaborator.lastName}`,
        collaboratorId: collaborator.id,
      });
    }
  }

  // Enum values and labels
  paymentFrequencyValues = BUSINESS_INSURANCE_PAYMENT_FREQUENCY;

  getPaymentFrequencyLabel = getPaymentFrequencyLabel;

  waitingDaysValues = BUSINESS_WAITING_DAYS_VALUES;

  getBusinessWaitingDaysLabel = getBusinessWaitingDaysLabel;

  coverageValues = BUSINESS_ACCIDENT_INSURANCE_COVERAGE_VALUES;

  getBusinessAccidentInsuranceCoverageLabel =
    getBusinessAccidentInsuranceCoverageLabel;

  override handleSubmit(): Observable<boolean> {
    // Merge shared business form data into the main metadata form
    const sharedData = this.sharedBusinessFormGroup.value;
    Object.keys(sharedData).forEach((key) => {
      this.metadataFormGroup.controls[key] =
        this.sharedBusinessFormGroup.controls[key];
    });
    this.metadataFormGroup.removeControl('sharedBusiness');
    return of(true);
  }
}
